import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, Loader2, CheckCircle2, Clock, XCircle, QrCode, Send, ArrowLeft } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import ImageUpload, { fileUrl } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import { canCreateTournaments, fmtMoney, fmtDate } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const PERIOD_LABEL = { month: "/ month", year: "/ year", one_time: "one-time" };

const STATUS_META = {
  pending: { l: "Under review", color: "#eab308", Icon: Clock },
  approved: { l: "Approved", color: "#22c55e", Icon: CheckCircle2 },
  rejected: { l: "Not approved", color: "#ef4444", Icon: XCircle },
};

export default function Subscribe() {
  const nav = useNavigate();
  const { user, checkAuth } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [requests, setRequests] = useState([]);
  const [productId, setProductId] = useState("");
  const [proofUrl, setProofUrl] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const alreadySubscribed = canCreateTournaments(user);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/config/subscription"),
        api.get("/subscriptions/my"),
      ]);
      setCfg(c);
      setRequests(r || []);
      if (c?.products?.length && !productId) setProductId(c.products[0].id);
    } catch (e) {
      setCfg(false);
    }
  };

  useEffect(() => {
    load();
    checkAuth?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pending = requests.find((r) => r.status === "pending");
  const latest = requests[0];

  const submit = async () => {
    if (!productId) return toast.error("Choose a plan");
    if (!proofUrl) return toast.error("Upload your payment proof screenshot first");
    setBusy(true);
    try {
      await api.post("/subscriptions/request", { product_id: productId, proof_url: proofUrl, note });
      toast.success("Request submitted — an admin will review your payment shortly.");
      setProofUrl(null);
      setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  const sym = cfg && cfg.currency;
  const selected = cfg?.products?.find((p) => p.id === productId);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Subscribe" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <button data-testid="subscribe-back" onClick={() => nav("/tournaments")}
            className="text-xs flex items-center gap-1.5 mb-4 hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}>
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Tournament Suite
          </button>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Unlock the Pro Suite</span>
          <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2 flex items-center gap-3">
            <Crown className="w-8 h-8 text-primary-c" /> Subscribe
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
            The Tournament Suite is a paid feature. Pay via the details below, upload your payment screenshot, and an admin will activate your account.
          </p>
        </motion.div>

        {cfg === null && <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>}
        {cfg === false && <p className="mt-10 text-sm" style={{ color: "var(--text-2)" }}>Could not load subscription details.</p>}

        {alreadySubscribed && (
          <div className="mt-6 rounded-2xl glow-border p-6 flex items-start gap-4" style={{ background: "var(--surface)" }} data-testid="already-subscribed">
            <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: "#22c55e" }} />
            <div>
              <h3 className="font-display font-bold">Your subscription is active</h3>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>
                You have full access to the Tournament Suite{user?.license_expires ? ` until ${fmtDate(user.license_expires)}` : ""}.
              </p>
              <button data-testid="go-tournaments" onClick={() => nav("/tournaments")}
                className="btn-glow rounded-full px-5 py-2.5 mt-4 text-sm font-bold">Open Tournament Suite</button>
            </div>
          </div>
        )}

        {cfg && cfg.enabled === false && !alreadySubscribed && (
          <div className="mt-6 rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }} data-testid="subscribe-disabled">
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Online subscriptions are currently closed. Please contact the PointControl team.</p>
          </div>
        )}

        {/* Latest request status */}
        {latest && (
          <RequestStatus req={latest} data-testid="my-request-status" />
        )}

        {cfg && cfg.enabled !== false && !alreadySubscribed && !pending && (
          <div className="mt-6 grid gap-5" data-testid="subscribe-form">
            {/* Plan picker */}
            <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
              <label className="text-xs uppercase tracking-wider mono" style={{ color: "var(--text-2)" }}>Choose your plan</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {(cfg.products || []).map((p) => (
                  <button key={p.id} data-testid={`plan-${p.id}`} onClick={() => setProductId(p.id)}
                    className={`text-left rounded-xl p-4 transition-all ${productId === p.id ? "glow-border ring-2 ring-lime-400" : "glass"}`}>
                    <div className="font-display font-bold">{p.name}</div>
                    <div className="mt-1 font-display font-black text-2xl">
                      {fmtMoney(sym, p.price)} <span className="text-xs font-normal" style={{ color: "var(--text-2)" }}>{PERIOD_LABEL[p.period] || ""}</span>
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: "var(--text-2)" }}>Valid for {p.duration_days} days</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment details + QR */}
            <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="payment-instructions">
              <h3 className="font-display font-bold flex items-center gap-2"><QrCode className="w-5 h-5 text-primary-c" /> How to pay</h3>
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-5 mt-4 items-start">
                {cfg.payment_qr_url ? (
                  <img data-testid="payment-qr" src={fileUrl(cfg.payment_qr_url)} alt="Payment QR"
                    className="w-full rounded-xl glow-border object-contain bg-white p-2" />
                ) : (
                  <div className="w-full aspect-square rounded-xl glow-border flex items-center justify-center" style={{ background: "var(--bg)" }}>
                    <QrCode className="w-10 h-10 opacity-30 text-primary-c" />
                  </div>
                )}
                <div>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-2)" }} data-testid="payment-details-text">
                    {cfg.payment_details || "Scan the QR code to pay, then upload your receipt below."}
                  </p>
                  {selected && (
                    <div className="mt-3 text-sm font-bold">
                      Amount to pay: <span className="text-primary-c">{fmtMoney(sym, selected.price)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Proof upload */}
            <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
              <ImageUpload value={proofUrl} onChange={setProofUrl} privateFile testid="proof-upload"
                label="Payment proof (screenshot)" aspect="aspect-[4/3]" hint="Your receipt — private, only admins can view" />
              <textarea data-testid="subscribe-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Reference number or note for the admin (optional)" rows={2}
                className="w-full mt-4 rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
              <button data-testid="submit-subscription" onClick={submit} disabled={busy}
                className="btn-glow rounded-full px-6 py-3 mt-4 font-bold inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for approval
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RequestStatus({ req }) {
  const m = STATUS_META[req.status] || STATUS_META.pending;
  return (
    <div className="mt-6 rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="my-request-status">
      <div className="flex items-center gap-3">
        <m.Icon className="w-6 h-6 shrink-0" style={{ color: m.color }} />
        <div>
          <div className="font-display font-bold flex items-center gap-2">
            {req.product_name}
            <span className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5" style={{ color: m.color }}>{m.l}</span>
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--text-2)" }}>Submitted {fmtDate(req.created_at)}</div>
        </div>
      </div>
      {req.status === "rejected" && req.admin_note && (
        <p className="text-sm mt-3 rounded-lg glass p-3" style={{ color: "var(--text-2)" }} data-testid="rejection-note">
          <span className="font-bold" style={{ color: "#ef4444" }}>Reason: </span>{req.admin_note}
        </p>
      )}
      {req.status === "approved" && req.license_expires && (
        <p className="text-sm mt-3" style={{ color: "var(--text-2)" }}>Active until <span className="font-bold" style={{ color: "var(--text)" }}>{fmtDate(req.license_expires)}</span>.</p>
      )}
      {req.status === "pending" && (
        <p className="text-sm mt-3" style={{ color: "var(--text-2)" }}>We're reviewing your payment. You'll be notified once it's approved.</p>
      )}
    </div>
  );
}
